import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { inventoryApi } from '@/lib/inventoryApi';
import { parseTypedAmount, toNumber } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { X, ArrowUpCircle, Package } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { AdjustStockRequest, InventoryItem } from '@/types/inventory';
import type { ApiListResponse } from '@/types/api';

type AdjustMode = 'set' | 'delta';

interface AdjustStockDrawerProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  preselectedItemId?: string;
  onSuccess?: () => void;
}

export function AdjustStockDrawer({
  open,
  onClose,
  farmId,
  preselectedItemId,
  onSuccess,
}: AdjustStockDrawerProps) {
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/25 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-[440px]',
          'bg-white shadow-xl border-l border-gray-200',
          'transform transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Unmount form when closed so state resets on reopen */}
        {open && (
          <AdjustStockForm
            farmId={farmId}
            preselectedItemId={preselectedItemId}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </>
  );
}

// Inner form component that resets via key remounting
interface AdjustStockFormProps {
  farmId: string;
  preselectedItemId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

function AdjustStockForm({
  farmId,
  preselectedItemId,
  onClose,
  onSuccess,
}: AdjustStockFormProps) {
  // Form state (initialized fresh on each mount)
  const [itemId, setItemId] = useState(preselectedItemId ?? '');
  const [mode, setMode] = useState<AdjustMode>('set');
  const [newQuantity, setNewQuantity] = useState('');
  const [quantityDelta, setQuantityDelta] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch items for dropdown
  const { data: itemsData, isLoading: itemsLoading } = useQuery<ApiListResponse<InventoryItem>>({
    queryKey: ['inventory', 'items', farmId, 'active'],
    queryFn: () => inventoryApi.listItems(farmId, { is_active: 'true', include_stock: 'true' }),
    staleTime: 60 * 1000,
  });

  const items = itemsData?.data ?? [];
  const selectedItem = items.find((i) => i.id === itemId);
  const currentStock = selectedItem?.stock ? toNumber(selectedItem.stock.quantity) : 0;

  // Typed text: "12,5" is 12.5, "12,500" is 12500, anything else is refused with a message.
  const activeParsed = parseTypedAmount(mode === 'set' ? newQuantity : quantityDelta, { thousands: true });
  const activeValue = 'value' in activeParsed ? activeParsed.value : null;
  const activeError = 'error' in activeParsed ? activeParsed.error : undefined;

  // Compute projected stock
  let projectedStock: number | null = null;
  if (activeValue !== null) {
    projectedStock = mode === 'set' ? activeValue : currentStock + activeValue;
  }

  // Submit mutation
  const mutation = useMutation({
    mutationFn: (data: AdjustStockRequest) => inventoryApi.adjustStock(farmId, data),
    onSuccess: () => {
      showSuccess('Stock adjusted successfully');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to adjust stock');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!itemId || !reason.trim()) return;

    const data: AdjustStockRequest = {
      item_id: itemId,
      reason: reason.trim(),
      ...(notes.trim() && { notes: notes.trim() }),
    };

    if (activeValue === null) return;
    if (mode === 'set') {
      data.new_quantity = activeValue;
    } else {
      data.quantity_delta = activeValue;
    }

    mutation.mutate(data);
  }

  const isFormValid =
    itemId &&
    reason.trim() &&
    activeValue !== null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-blue-50 p-2">
            <ArrowUpCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Adjust Stock</h2>
            <p className="text-sm text-gray-500">Correct stock levels</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Item selector */}
        {itemsLoading ? (
          <div className="flex items-center gap-2 py-4">
            <LoadingSpinner size={16} />
            <span className="text-sm text-gray-500">Loading items...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <Package className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No active items. Create items first.</p>
          </div>
        ) : (
          <Select
            label="Item"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="Select an item..."
            options={items.map((item) => ({
              value: item.id,
              label: `${item.name} (${item.unit})`,
            }))}
          />
        )}

        {/* Current stock indicator */}
        {selectedItem && (
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Current Stock</span>
              <span className="font-medium text-gray-900">
                {currentStock.toLocaleString()} {selectedItem.unit}
              </span>
            </div>
            {projectedStock !== null && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-500">After Adjustment</span>
                <span
                  className={cn(
                    'font-medium',
                    projectedStock < 0
                      ? 'text-red-600'
                      : projectedStock > currentStock
                        ? 'text-green-600'
                        : projectedStock < currentStock
                          ? 'text-yellow-600'
                          : 'text-gray-900'
                  )}
                >
                  {projectedStock.toLocaleString()} {selectedItem.unit}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Adjustment mode toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Adjustment Method
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('set')}
              className={cn(
                'px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                mode === 'set'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              Set exact quantity
            </button>
            <button
              type="button"
              onClick={() => setMode('delta')}
              className={cn(
                'px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                mode === 'delta'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              Add / Subtract
            </button>
          </div>
        </div>

        {/* Quantity input based on mode */}
        {mode === 'set' ? (
          <Input
            label="New Quantity"
            type="text"
            inputMode="decimal"
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            error={activeError}
            placeholder="Enter exact stock count"
            required
            description="The stock will be set to this exact amount"
          />
        ) : (
          <Input
            label="Quantity Change"
            type="text"
            inputMode="decimal"
            value={quantityDelta}
            onChange={(e) => setQuantityDelta(e.target.value)}
            error={activeError}
            placeholder="e.g., -5 or +10"
            required
            description="Use negative values to reduce stock"
          />
        )}

        {/* Reason (required) */}
        <Select
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Select a reason..."
          options={[
            { value: 'physical_count', label: 'Physical Count / Reconciliation' },
            { value: 'damaged', label: 'Damaged Goods' },
            { value: 'expired', label: 'Expired Products' },
            { value: 'spillage', label: 'Spillage / Waste' },
            { value: 'theft', label: 'Theft / Loss' },
            { value: 'data_correction', label: 'Data Entry Correction' },
            { value: 'other', label: 'Other' },
          ]}
        />

        {/* Notes */}
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional details about this adjustment..."
          rows={3}
        />

        {/* Warning if setting negative */}
        {projectedStock !== null && projectedStock < 0 && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
            <p className="text-sm text-red-700">
              Warning: This adjustment would result in a negative stock balance.
            </p>
          </div>
        )}
      </form>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
        <Button
          variant="primary"
          fullWidth
          loading={mutation.isPending}
          disabled={!isFormValid}
          onClick={handleSubmit as unknown as () => void}
        >
          Adjust Stock
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
